import {Station} from "../../utils/model/Station";
import {
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {gethttpDeletedItemsAdmin, restoreFuelAdmin, restoreStationAdmin} from "../../utils/Api";
import ErrorResponseDialog from "../station/components/ErrorResponseDialog";
import {FuelType} from "../../utils/model/FuelType";
import {FuelCollection} from "../../utils/model/FuelCollection";
import Button from "@mui/material/Button";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {useNavigate} from "react-router-dom";

export default function AdminStationsRemoved() {
    const [stations, setStations] = useState<Station[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<TemplateStringsArray | undefined>();
    const navigate = useNavigate();
    const {i18n} = useTranslation();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [search, setSearch] = useState("");

    const memoizedGetDeletedItemsAdmin = useMemo(() => {
        return async () => {
            setLoading(true);
            try {
                const result = await gethttpDeletedItemsAdmin();
                setStations(result as Station[]);
            } catch (error: any) {
                setError(error.message);
            }
            setLoading(false);
        }
    }, []);

    const onGetDeletedStations = async () => {
        setLoading(true);
        try {
            const result = await gethttpDeletedItemsAdmin();
            setStations(result as Station[]);
        } catch (error: any) {
            setError(error.message);
        }
        setLoading(false);
    }

    useEffect(() => {
        memoizedGetDeletedItemsAdmin();
    }, [memoizedGetDeletedItemsAdmin]);

    const onRestoreStation = async (stationId: string) => {
        setLoading(true);
        try {
            await restoreStationAdmin(stationId);
            await onGetDeletedStations();
        } catch (error: any) {
            setError(error.message);
        }
        setLoading(false);
    }

    const onRestoreFuel = async (stationId: string, fuelType: FuelType) => {
        setLoading(true);
        try {
            await restoreFuelAdmin(stationId, fuelType);
            await onGetDeletedStations();
        } catch (error: any) {
            setError(error.message);
        }
        setLoading(false);
    }

    const getDeletedFuelTypes = (station: Station): FuelType[] => {
        if (!station.fuelCollection) { return []; }
        return Object.keys(station.fuelCollection)
            .filter(type => station.fuelCollection[type as keyof FuelCollection]?.isDeleted)
            .map(type => type as FuelType);
    }

    const getStationUrl = (station: Station, deletedFuels: FuelType[]): string => {
        const fuelId = deletedFuels[0] ?? FuelType.SP95;
        return `/${i18n.language}/station/${station.id}/${fuelId}`;
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }

    const filteredStations = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) { return stations; }
        return stations.filter(s =>
            s.displayName?.toLowerCase().includes(needle)
            || s.brand?.toLowerCase().includes(needle)
            || s.formattedAddress?.toLowerCase().includes(needle)
        );
    }, [stations, search]);

    const paginatedStations = filteredStations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <>
            {error &&
                <ErrorResponseDialog errorCode={error} onClose={() => setError(undefined)}/>
            }
            <Stack direction='column' height={'100%'} sx={{padding: "1rem"}}>
                {loading &&
                    <CircularProgress
                        color="secondary"
                        size={64}
                        disableShrink
                        thickness={4}
                        sx={{margin: "auto"}}
                    />
                }
                {!loading &&
                    <Stack direction='column' gap={1} marginBottom={1}>
                        <TextField
                            size="small"
                            placeholder="Search by name, brand or address"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                            sx={{maxWidth: 400}}
                            InputProps={{
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => { setSearch(""); setPage(0); }}
                                            aria-label="Clear search">
                                            <ClearIcon fontSize="small"/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : undefined
                            }}
                        />
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Display Name</TableCell>
                                        <TableCell>Brand</TableCell>
                                        <TableCell>Address</TableCell>
                                        <TableCell>Scope</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedStations.map((station: Station) => {
                                        const deletedFuels = getDeletedFuelTypes(station);
                                        const stationUrl = getStationUrl(station, deletedFuels);
                                        return <TableRow key={station.id} sx={{
                                            '&:last-child td, &:last-child th': {border: 0},
                                        }}>
                                            <TableCell>
                                                <Stack direction="row" alignItems="center" gap={0.5}>
                                                    {station.displayName}
                                                    <IconButton
                                                        size="small"
                                                        href={stationUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer">
                                                        <OpenInNewIcon fontSize="inherit"/>
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                            <TableCell>{station.brand}</TableCell>
                                            <TableCell>{station.formattedAddress}</TableCell>
                                            <TableCell>
                                                {station.isDeleted
                                                    ? <Chip label="STATION" color="error" size="small"/>
                                                    : <Stack direction="row" gap={0.5} flexWrap="wrap">
                                                        {deletedFuels.map(fuelType =>
                                                            <Chip key={fuelType} label={fuelType} color="warning" size="small"/>
                                                        )}
                                                    </Stack>
                                                }
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                                                    {station.isDeleted &&
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            size="small"
                                                            onClick={() => onRestoreStation(station.id)}>
                                                            Restore Station
                                                        </Button>
                                                    }
                                                    {!station.isDeleted && deletedFuels.map(fuelType =>
                                                        <Button
                                                            key={fuelType}
                                                            variant="outlined"
                                                            color="success"
                                                            size="small"
                                                            onClick={() => onRestoreFuel(station.id, fuelType)}>
                                                            Restore {fuelType}
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    })}
                                    {paginatedStations.length === 0 &&
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                <Typography variant="body2" color="text.secondary">No deleted items</Typography>
                                            </TableCell>
                                        </TableRow>
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {filteredStations.length > rowsPerPage &&
                            <TablePagination
                                component="div"
                                count={filteredStations.length}
                                page={page}
                                onPageChange={(_e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[25, 50, 100, 200]}
                            />
                        }
                    </Stack>
                }
                <Stack sx={{flexDirection: "row", gap: 1}}>
                    <Button
                        variant="contained"
                        type="button"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIcon/>
                    </Button>
                </Stack>
            </Stack>
        </>
    )
}
